import { Loader } from '@lifebit-ai/lds'
import React from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'

import useUser from 'hooks/useUser'
import NotFound from 'modules/general/not-found/NotFound'
import ExportAndAccessRequestDetails from 'modules/requests/ExportAndAccessRequestDetails'
import ImportRequestDetails from 'modules/requests/ImportRequestDetails'
import RequestsPage from 'modules/requests/RequestsPage'
import DataAccessRequestDetails from 'modules/requests/data-access-requests/DataAccessRequestDetails'
import {
  LEGACY_EXPORT_ANALYSIS_LIST_PATH,
  LEGACY_EXPORT_FILES_LIST_PATH,
  LEGACY_IMPORT_REQUESTS_LIST_PATH,
  LEGACY_REQUESTS_BASE_PATH,
  LEGACY_VIEW_PATH,
  V3_DATA_ACCESS_REQUESTS_LIST_PATH,
  V3_EXPORT_ANALYSIS_LIST_PATH,
  V3_EXPORT_FILES_LIST_PATH,
  V3_IMPORT_REQUESTS_LIST_PATH,
  V3_LEGACY_VIEW_PATH,
  V3_REQUESTS_BASE_PATH,
  getExportAndAccessRequestDetailsPath,
  getImportRequestDetailsPath,
  getLegacyExportAndAccessRequestDetailsPath,
  getLegacyViewRedirectPath,
  isAirlockRequestsV3RoutesEnabled
} from 'modules/requests/requestDetailPaths'
import { RequestsTabKeys } from 'modules/requests/requestsTabKeys'
import { useGetImportRequestVerification } from 'services/import-requests/hooks/useGetImportRequestVerification'

import { LoadingPage } from './AirlockRoutes.styles'
import LoginMagicLink from './modules/login/LoginMagicLink'
import LoginMagicLinkCallback from './modules/login/LoginMagicLinkCallback'

/**
 * Represents a route that should only be viewed by a logged in user. If the user is not logged in, redirect to Login page.
 *  Users without teams are redirected to a page to create a team, unless `avoidWorkspacesRedirect=true` is specified.
 *
 * If a user has an invite token present and "staged" for accepting - the route redirects the user to accept it and removes
 *  the staged token from Local Storage.
 */
const PrivateRoute: React.FC<{ children: React.ReactNode; avoidWorkspacesRedirect?: boolean }> = ({ children }) => {
  const { user, isLoading: isLoadingUser } = useUser()
  if (isLoadingUser)
    return (
      <LoadingPage data-testid="app-loading">
        <Loader text="Loading..." />
      </LoadingPage>
    )
  // In case the user does not exist, just navigate them to the login page.
  if (!user) return <Navigate to="/login" />

  return <>{children}</>
}

/**
 * Represents a route for guests. This prevents logged-in users from accessing guest pages.
 */
const StrictlyPublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: isLoadingUser } = useUser()
  if (isLoadingUser)
    return (
      <LoadingPage data-testid="app-loading">
        <Loader text="Loading..." />
      </LoadingPage>
    )
  if (!user) return <>{children}</>
  return <Navigate to="/" replace />
}

export const LEGACY_EXPORT_REQUEST_REDIRECT_LOADER_TEST_ID = 'legacy-export-request-redirect-loader'

const LegacyExportRequestRedirect = (): JSX.Element => {
  const { resultId } = useParams<{ resultId: string }>()
  const {
    data: importRequest,
    isLoading,
    isError
  } = useGetImportRequestVerification({
    requestId: resultId ?? '',
    options: {
      retry: false,
      onError: () => {}
    }
  })

  if (!resultId) return <NotFound />

  if (isLoading) {
    return (
      <LoadingPage data-testid={LEGACY_EXPORT_REQUEST_REDIRECT_LOADER_TEST_ID}>
        <Loader text="Loading..." />
      </LoadingPage>
    )
  }

  const redirectPath =
    isError || !importRequest ? getExportAndAccessRequestDetailsPath(resultId) : getImportRequestDetailsPath(resultId)

  return <Navigate to={redirectPath} replace />
}

const LegacyDataAccessRequestDetailsRedirect = (): JSX.Element => {
  const { requestId } = useParams<{ requestId: string }>()

  if (!requestId) return <NotFound />

  return <Navigate to={getLegacyExportAndAccessRequestDetailsPath(requestId)} replace />
}

const LegacyViewPathRedirect = (): JSX.Element => {
  const { resultId } = useParams<{ resultId: string }>()

  if (!resultId) return <NotFound />

  return <Navigate to={`${getLegacyViewRedirectPath()}/${resultId}`} replace />
}

const requestsPageElement = (
  <PrivateRoute>
    <RequestsPage />
  </PrivateRoute>
)

const dataAccessRequestDetailsElement = (
  <PrivateRoute>
    <DataAccessRequestDetails />
  </PrivateRoute>
)

const exportRequestDetailsElement = (
  <PrivateRoute>
    <ExportAndAccessRequestDetails />
  </PrivateRoute>
)

const importRequestDetailsElement = (
  <PrivateRoute>
    <ImportRequestDetails />
  </PrivateRoute>
)

const renderRequestsPageRoute = (canonicalPath: string, aliasPath: string): JSX.Element => {
  const isV3RoutesEnabled = isAirlockRequestsV3RoutesEnabled()

  return (
    <>
      <Route
        path={canonicalPath}
        element={isV3RoutesEnabled ? requestsPageElement : <Navigate to={aliasPath} replace />}
      />
      <Route
        path={aliasPath}
        element={isV3RoutesEnabled ? <Navigate to={canonicalPath} replace /> : requestsPageElement}
      />
    </>
  )
}

const renderExportRequestDetailsRoute = (canonicalPath: string, aliasPath: string): JSX.Element => {
  const isV3RoutesEnabled = isAirlockRequestsV3RoutesEnabled()

  return (
    <>
      <Route
        path={canonicalPath}
        element={isV3RoutesEnabled ? exportRequestDetailsElement : <Navigate to={aliasPath} replace />}
      />
      <Route
        path={aliasPath}
        element={isV3RoutesEnabled ? <Navigate to={canonicalPath} replace /> : exportRequestDetailsElement}
      />
    </>
  )
}

const renderImportRequestDetailsRoute = (canonicalPath: string, aliasPath: string): JSX.Element => {
  const isV3RoutesEnabled = isAirlockRequestsV3RoutesEnabled()

  return (
    <>
      <Route
        path={canonicalPath}
        element={isV3RoutesEnabled ? importRequestDetailsElement : <Navigate to={aliasPath} replace />}
      />
      <Route
        path={aliasPath}
        element={isV3RoutesEnabled ? <Navigate to={canonicalPath} replace /> : importRequestDetailsElement}
      />
    </>
  )
}

export const AirlockRoutes: React.FC = () => {
  const isV3RoutesEnabled = isAirlockRequestsV3RoutesEnabled()
  const defaultListPath = isV3RoutesEnabled ? V3_EXPORT_FILES_LIST_PATH : LEGACY_EXPORT_FILES_LIST_PATH
  const defaultBasePath = isV3RoutesEnabled ? V3_REQUESTS_BASE_PATH : LEGACY_REQUESTS_BASE_PATH

  return (
    <Routes>
      <Route path="*" element={<NotFound />} />
      <Route
        path="/login"
        element={
          <StrictlyPublicRoute>
            <LoginMagicLink />
          </StrictlyPublicRoute>
        }
      />
      <Route path="/auth/magic-login/callback" element={<LoginMagicLinkCallback />} />

      <Route path="/" element={<Navigate to={defaultListPath} replace />} />
      <Route path="/dashboard" element={<Navigate to={defaultListPath} replace />} />
      <Route path={defaultBasePath} element={<Navigate to={defaultListPath} replace />} />
      <Route
        path={`${V3_REQUESTS_BASE_PATH}/${RequestsTabKeys.Export}`}
        element={<Navigate to={V3_EXPORT_FILES_LIST_PATH} replace />}
      />
      <Route path={`${V3_REQUESTS_BASE_PATH}/files`} element={<Navigate to={V3_EXPORT_FILES_LIST_PATH} replace />} />
      <Route
        path={`${V3_REQUESTS_BASE_PATH}/analysis`}
        element={<Navigate to={V3_EXPORT_ANALYSIS_LIST_PATH} replace />}
      />
      <Route path="/export-requests" element={<Navigate to={LEGACY_EXPORT_FILES_LIST_PATH} replace />} />
      <Route path="/export-requests/files" element={<Navigate to={LEGACY_EXPORT_FILES_LIST_PATH} replace />} />
      <Route path="/export-requests/analysis" element={<Navigate to={LEGACY_EXPORT_ANALYSIS_LIST_PATH} replace />} />
      <Route path={`${V3_REQUESTS_BASE_PATH}/files`} element={<Navigate to={V3_EXPORT_FILES_LIST_PATH} replace />} />
      <Route
        path={`${V3_REQUESTS_BASE_PATH}/analysis`}
        element={<Navigate to={V3_EXPORT_ANALYSIS_LIST_PATH} replace />}
      />

      {renderRequestsPageRoute(V3_EXPORT_FILES_LIST_PATH, LEGACY_EXPORT_FILES_LIST_PATH)}
      {renderRequestsPageRoute(V3_EXPORT_ANALYSIS_LIST_PATH, LEGACY_EXPORT_ANALYSIS_LIST_PATH)}
      {isV3RoutesEnabled ? (
        <Route path={V3_DATA_ACCESS_REQUESTS_LIST_PATH} element={requestsPageElement} />
      ) : (
        <Route
          path={V3_DATA_ACCESS_REQUESTS_LIST_PATH}
          element={<Navigate to={LEGACY_EXPORT_FILES_LIST_PATH} replace />}
        />
      )}
      {renderRequestsPageRoute(V3_IMPORT_REQUESTS_LIST_PATH, LEGACY_IMPORT_REQUESTS_LIST_PATH)}

      {renderExportRequestDetailsRoute(
        `${V3_REQUESTS_BASE_PATH}/${RequestsTabKeys.Export}/:requestId`,
        `${LEGACY_REQUESTS_BASE_PATH}/${RequestsTabKeys.ExportAndAccess}/:requestId`
      )}
      {isV3RoutesEnabled ? (
        <Route
          path={`${V3_REQUESTS_BASE_PATH}/${RequestsTabKeys.DataAccess}/:requestId`}
          element={dataAccessRequestDetailsElement}
        />
      ) : (
        <Route
          path={`${V3_REQUESTS_BASE_PATH}/${RequestsTabKeys.DataAccess}/:requestId`}
          element={<LegacyDataAccessRequestDetailsRedirect />}
        />
      )}
      {renderImportRequestDetailsRoute(
        `${V3_REQUESTS_BASE_PATH}/${RequestsTabKeys.Import}/:requestId`,
        `${LEGACY_REQUESTS_BASE_PATH}/${RequestsTabKeys.Import}/:requestId`
      )}

      <Route path={`${V3_LEGACY_VIEW_PATH}/:resultId`} element={<LegacyExportRequestRedirect />} />
      <Route path={`${LEGACY_VIEW_PATH}/:resultId`} element={<LegacyViewPathRedirect />} />
    </Routes>
  )
}

export default AirlockRoutes
